/**
 * ProdutosPage Component
 * 
 * Página de listagem e gerenciamento de produtos.
 * Permite visualizar, criar, editar e excluir produtos.
 * 
 * Funcionalidades:
 * - Listagem de produtos com paginação
 * - Filtros de busca
 * - Modal de criação/edição
 * - Seleção de grupo, marca, unidade
 * - Controle de estoque mínimo/máximo
 * - Exclusão com confirmação
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus, Search, Package, MoreVertical, X, Eye, Edit, Trash2, Link2, Tag, Check, Image as ImageIcon
} from 'lucide-react'
import DataTable from '../../../../components/ERP/DataTable/DataTable'
import Modal from '../../../../components/ERP/Modal/Modal'
import FormField from '../../../../components/ERP/Form/FormField'
import Toast from '../../../../components/Toast/Toast'
import ERPSelectSearchModal, {
  ERPSelectModalItem,
  ERPSelectModalEmpty
} from '../../../../components/ERP/Modal/ERPSelectSearchModal'
import { productService } from '../../../../services/erpApi'
import { useProductCacheInvalidation } from '../../../../hooks/useProductCacheInvalidation'
import { useErpProductCache } from '../../../../hooks/useErpProductCache'
import { useToast } from '../../../../hooks/useToast'
import { useAuth } from '../../../../contexts/AuthContext'
import { usePermissions, MODULES, ACTIONS } from '../../../../hooks/usePermissions'
import { copyStorefrontProductUrl } from '../../../../utils/storefrontProductUrl'
import QrCodeScannerButton from '../../../../components/ERP/QrCodeScanner/QrCodeScannerButton'
import {
  matchesProductSearch,
  matchesProductBrand
} from '../../../../utils/productListSearch'
import './ProdutosPage.css'

function formatCacheSyncLabel(ts) {
  if (!ts) return null
  try {
    return new Date(ts).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return null
  }
}

const NAME_INDICATORS_KEY = 'wfmed.produtos.nameIndicators'

function readNameIndicatorsEnabled() {
  try {
    return localStorage.getItem(NAME_INDICATORS_KEY) === '1'
  } catch {
    return false
  }
}

function hasProductDescription(produto) {
  const ecommerce = String(produto?.ecommerce_description || '').trim()
  const internal = String(produto?.description || '').trim()
  return ecommerce.length > 0 || internal.length > 0
}

function getProductPhotoCount(produto) {
  if (produto?.image_count !== undefined && produto?.image_count !== null && produto?.image_count !== '') {
    const count = Number(produto.image_count)
    return Number.isFinite(count) ? count : 0
  }
  return produto?.image_url ? 1 : 0
}

function ProdutosPage() {
  const navigate = useNavigate()
  const { toast, showToast, hideToast } = useToast()
  const { isAdmin } = useAuth()
  const { hasPermission } = usePermissions()
  const canEditDescription = hasPermission(MODULES.PRODUCTS, ACTIONS.PRODUCTS_EDIT_DESCRIPTION)
  const canEditBrand = isAdmin() || hasPermission(MODULES.PRODUCTS, ACTIONS.PRODUCTS_EDIT_BRAND)
  const canEditGroup = isAdmin() || hasPermission(MODULES.PRODUCTS, ACTIONS.PRODUCTS_EDIT_GROUP)
  const hideProductCost = !isAdmin() && hasPermission(MODULES.PRODUCTS, ACTIONS.PRODUCTS_HIDE_COST)
  const [showNameIndicators, setShowNameIndicators] = useState(readNameIndicatorsEnabled)
  const [produtos, setProdutos] = useState([])
  const [catalog, setCatalog] = useState([]) // Catálogo completo em memória (cache)
  const [allProdutos, setAllProdutos] = useState([]) // Filtrado (busca) — base da paginação
  const [grupos, setGrupos] = useState([])
  const [marcas, setMarcas] = useState([])
  const [unidades, setUnidades] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingProduto, setEditingProduto] = useState(null)
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(50) // 50 itens por página por padrão
  
  const {
    notifyProductUpdated,
    notifyProductDeleted,
    notifyGroupChanged
  } = useProductCacheInvalidation()
  const {
    getProducts,
    getGroups,
    getBrands,
    getUnits,
    isOffline,
    isStale,
    revalidating,
    lastSyncedAt,
  } = useErpProductCache()
  const [formData, setFormData] = useState({
    sku: '',
    barcode: '',
    name: '',
    description: '',
    costPrice: '',
    price: '',
    groupId: '',
    brandId: '',
    unitId: '',
    minStock: '',
    maxStock: '',
    active: true
  })
  const [errors, setErrors] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedBrand, setSelectedBrand] = useState(null)
  const [brandModalOpen, setBrandModalOpen] = useState(false)
  const [brandSearchTerm, setBrandSearchTerm] = useState('')

  // Estado para controlar qual dropdown está aberto
  const [openDropdownId, setOpenDropdownId] = useState(null)
  const [openItemsPerPageDropdown, setOpenItemsPerPageDropdown] = useState(false)
  const itemsPerPageMenuRef = useRef(null)
  const prevRevalidating = useRef(false)

  /** Carrega catálogo uma vez (memória / IndexedDB / API) — busca NÃO refaz request. */
  const loadCatalog = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const produtosData = await getProducts()
      setCatalog(Array.isArray(produtosData) ? produtosData : [])
    } catch (error) {
      console.error('Erro ao carregar produtos:', error)
      setLoadError(
        error?.message ||
          'Não foi possível carregar os produtos. Verifique a conexão.'
      )
    } finally {
      setLoading(false)
    }
  }, [getProducts])

  // Filtra só em memória (busca por tokens + marca)
  useEffect(() => {
    setAllProdutos(
      catalog.filter(
        (p) => matchesProductSearch(p, searchTerm) && matchesProductBrand(p, selectedBrand)
      )
    )
  }, [catalog, searchTerm, selectedBrand])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, selectedBrand])

  const filteredBrands = useMemo(() => {
    const term = brandSearchTerm.trim().toLowerCase()
    const list = Array.isArray(marcas) ? marcas : []
    if (!term) return list
    return list.filter((b) => String(b.name || '').toLowerCase().includes(term))
  }, [marcas, brandSearchTerm])

  const openBrandModal = () => {
    setBrandSearchTerm('')
    setBrandModalOpen(true)
  }

  const toggleNameIndicators = () => {
    setShowNameIndicators((current) => {
      const next = !current
      try {
        localStorage.setItem(NAME_INDICATORS_KEY, next ? '1' : '0')
      } catch {
        /* preferência local indisponível */
      }
      return next
    })
  }

  const clearBrandFilter = () => {
    setSelectedBrand(null)
  }

  // Calcular produtos paginados
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const paginatedData = allProdutos.slice(startIndex, endIndex)
    setProdutos(paginatedData)
  }, [allProdutos, currentPage, itemsPerPage])

  const loadOptions = useCallback(async () => {
    try {
      const [gruposData, marcasData, unidadesData] = await Promise.all([
        getGroups(),
        getBrands(),
        getUnits()
      ])

      setGrupos(gruposData || [])
      setMarcas(marcasData || [])
      setUnidades(unidadesData || [])
    } catch (error) {
      console.error('Erro ao carregar opções:', error)
    }
  }, [getGroups, getBrands, getUnits])

  useEffect(() => {
    loadCatalog()
    loadOptions()
  }, [loadCatalog, loadOptions])

  // Após revalidação em background, atualiza a lista sem loading full-page
  useEffect(() => {
    if (prevRevalidating.current && !revalidating) {
      getProducts()
        .then((data) => setCatalog(Array.isArray(data) ? data : []))
        .catch(() => {})
    }
    prevRevalidating.current = revalidating
  }, [revalidating, getProducts])

  const loadProdutos = loadCatalog

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    try {
      const dataToSend = {
        sku: formData.sku,
        barcode: formData.barcode || null,
        name: formData.name,
        ...(canEditDescription ? { description: formData.description || null } : {}),
        ...(hideProductCost
          ? {}
          : { costPrice: formData.costPrice ? parseFloat(formData.costPrice) : null }),
        price: parseFloat(formData.price),
        ...(canEditGroup ? { groupId: formData.groupId || null } : {}),
        ...(canEditBrand ? { brandId: formData.brandId || null } : {}),
        unitId: formData.unitId || null,
        minStock: formData.minStock ? parseInt(formData.minStock) : null,
        maxStock: formData.maxStock ? parseInt(formData.maxStock) : null,
        active: formData.active
      }

      if (!editingProduto) {
        setErrors({ submit: 'A criação de produtos deve ser feita por solicitação' })
        return
      }

      let response
      const oldGroupId = editingProduto?.group_id
      const newGroupId = dataToSend.groupId ? parseInt(dataToSend.groupId) : null
      
      response = await productService.update(editingProduto.id, dataToSend)
        
      if (response.success) {
        // Notifica atualização do produto
        notifyProductUpdated(editingProduto.id, response.data)
          
        // Se o grupo mudou, notifica mudança de grupo
        if (oldGroupId !== newGroupId) {
          notifyGroupChanged(editingProduto.id, oldGroupId, newGroupId)
        }
      }

      if (response.success) {
        setModalOpen(false)
        resetForm()
        loadProdutos()
      } else {
        if (response.errors && Array.isArray(response.errors)) {
          const errorObj = {}
          response.errors.forEach(err => {
            errorObj[err.param || err.path] = err.msg || err.message
          })
          setErrors(errorObj)
        } else {
          setErrors({ submit: response.message || 'Erro ao salvar produto' })
        }
      }
    } catch (error) {
      console.error('Erro ao salvar produto:', error)
      const errorMessage = error.response?.data?.message || 'Erro ao salvar produto'
      const errorDetails = error.response?.data?.errors
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const errorObj = {}
        errorDetails.forEach(err => {
          errorObj[err.param || err.path] = err.msg || err.message
        })
        setErrors(errorObj)
      } else {
        setErrors({ submit: errorMessage })
      }
    }
  }

  const handleCopyEcommerceLink = async (produto) => {
    try {
      await copyStorefrontProductUrl(produto.id)
      showToast('Link do e-commerce copiado', 'success')
    } catch (error) {
      console.error('Erro ao copiar link do e-commerce:', error)
      showToast(error.message || 'Não foi possível copiar o link', 'error')
    }
  }

  const handleEdit = (produto) => {
    setEditingProduto(produto)
    setFormData({
      sku: produto.sku || '',
      barcode: produto.barcode || '',
      name: produto.name || '',
      description: produto.description || '',
      costPrice: produto.cost_price || '',
      price: produto.price || '',
      groupId: produto.group_id || '',
      brandId: produto.brand_id || '',
      unitId: produto.unit_id || '',
      minStock: produto.min_stock || '',
      maxStock: produto.max_stock || '',
      active: produto.active !== undefined ? produto.active : true
    })
    setModalOpen(true)
  }


  const handleDelete = async (produto) => {
    if (window.confirm(`Deseja realmente excluir o produto ${produto.name}?`)) {
      try {
        const productId = produto.id
        const response = await productService.delete(productId)
        if (response.success) {
          // Notifica exclusão do produto
          notifyProductDeleted(productId)
          loadProdutos()
        }
      } catch (error) {
        console.error('Erro ao excluir produto:', error)
        alert('Erro ao excluir produto')
      }
    }
  }

  const handleNewProduct = () => {
    navigate('/erp/produtos/solicitacoes/nova')
  }

  const resetForm = () => {
    setFormData({
      sku: '',
      barcode: '',
      name: '',
      description: '',
      costPrice: '',
      price: '',
      groupId: '',
      brandId: '',
      unitId: '',
      minStock: '',
      maxStock: '',
      active: true
    })
    setEditingProduto(null)
    setErrors({})
  }

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const columns = [
    { key: 'sku', label: 'SKU', sortable: true },
    {
      key: 'name',
      label: 'Nome',
      sortable: true,
      render: (value, row) => (
        <span className="produto-name-cell">
          <span className="produto-name-cell__text">{value}</span>
          {showNameIndicators && (
            <span className="produto-name-indicators">
              <span className="produto-photo-count" title={`${getProductPhotoCount(row)} foto(s)`}>
                <ImageIcon size={12} aria-hidden />
                {getProductPhotoCount(row)}
              </span>
              {!hasProductDescription(row) && (
                <span className="produto-no-description-dot" title="Sem descrição" />
              )}
            </span>
          )}
        </span>
      )
    },
    { 
      key: 'price', 
      label: 'Preço',
      render: (value) => formatCurrency(value)
    },
    { 
      key: 'stock_quantity', 
      label: 'Estoque',
      render: (value) => (
        <span className={value <= 0 ? 'stock-low' : ''}>
          {value || 0}
        </span>
      )
    },
    {
      key: 'brand_name',
      label: 'Marca',
      render: (value) => value || '-'
    }
  ]

  const handleView = (produto) => {
    const url = `/erp/produtos/produtos/${produto.id}`
    window.open(url, '_blank')
  }


  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('.product-actions-cell')) {
        setOpenDropdownId(null)
      }
      if (openItemsPerPageDropdown && itemsPerPageMenuRef.current && !itemsPerPageMenuRef.current.contains(event.target)) {
        setOpenItemsPerPageDropdown(false)
      }
    }

    if (openDropdownId || openItemsPerPageDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openDropdownId, openItemsPerPageDropdown])

  // Adicionar coluna de ações com dropdown
  const columnsWithActions = [
    ...columns,
    {
      key: 'actions',
      label: '',
      render: (value, row) => {
        return (
          <div className="product-actions-cell">
            <div className="menu-wrapper">
              <button
                className={`menu-trigger ${openDropdownId === row.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation()
                  setOpenDropdownId(openDropdownId === row.id ? null : row.id)
                }}
              >
                <MoreVertical size={20} />
              </button>
              {openDropdownId === row.id && (
                <div 
                  className="dropdown-menu"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <button 
                    className="dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation()
                      setOpenDropdownId(null)
                      handleView(row)
                    }}
                  >
                    <Eye size={14} />
                    Visualizar
                  </button>
                  <button 
                    className="dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation()
                      setOpenDropdownId(null)
                      handleEdit(row)
                    }}
                  >
                    <Edit size={14} />
                    Editar
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation()
                      setOpenDropdownId(null)
                      handleCopyEcommerceLink(row)
                    }}
                  >
                    <Link2 size={14} />
                    Copiar link do e-commerce
                  </button>
                  <div className="dropdown-divider" />
                  <button 
                    className="dropdown-item danger"
                    onClick={(e) => {
                      e.stopPropagation()
                      setOpenDropdownId(null)
                      handleDelete(row)
                    }}
                  >
                    <Trash2 size={14} />
                    Excluir
                  </button>
                </div>
              )}
            </div>
          </div>
        )
      }
    }
  ]

  return (
    <div className="produtos-page">
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={hideToast} />
      <div className="page-header">
        <div>
          <h1>
            <Package size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Produtos
          </h1>
          <p>Gerencie seus produtos</p>
          <div className="produtos-cache-status" aria-live="polite">
            {isOffline && (
              <span className="produtos-cache-badge produtos-cache-badge--offline">
                Offline — catálogo em cache
              </span>
            )}
            {!isOffline && revalidating && (
              <span className="produtos-cache-badge produtos-cache-badge--sync">
                Atualizando…
              </span>
            )}
            {!isOffline && !revalidating && isStale && catalog.length > 0 && (
              <span className="produtos-cache-badge produtos-cache-badge--stale">
                Cache local
                {formatCacheSyncLabel(lastSyncedAt)
                  ? ` · sync ${formatCacheSyncLabel(lastSyncedAt)}`
                  : ''}
              </span>
            )}
            {!isOffline && !revalidating && !isStale && lastSyncedAt && (
              <span className="produtos-cache-badge produtos-cache-badge--ok">
                Sincronizado {formatCacheSyncLabel(lastSyncedAt)}
              </span>
            )}
          </div>
        </div>
        <div className="produtos-page__header-actions">
          <button
            type="button"
            className={`produtos-indicator-toggle${showNameIndicators ? ' is-on' : ''}`}
            onClick={toggleNameIndicators}
            aria-pressed={showNameIndicators}
            title="Mostra a quantidade de fotos e um ponto laranja quando o produto está sem descrição. A escolha fica salva neste navegador."
          >
            <span className="produtos-indicator-toggle__track" aria-hidden>
              <span className="produtos-indicator-toggle__thumb" />
            </span>
            <span>Indicadores</span>
          </button>
          <button
            type="button"
            className={`produtos-brand-filter-btn${selectedBrand ? ' is-active' : ''}`}
            onClick={openBrandModal}
            title="Filtrar por marca"
          >
            <Tag size={18} />
            <span className="produtos-brand-filter-btn__label">
              {selectedBrand ? selectedBrand.name : 'Marca'}
            </span>
            {selectedBrand ? (
              <span
                className="produtos-brand-filter-btn__clear"
                role="button"
                tabIndex={0}
                aria-label="Limpar filtro de marca"
                onClick={(e) => {
                  e.stopPropagation()
                  clearBrandFilter()
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    clearBrandFilter()
                  }
                }}
              >
                <X size={14} />
              </span>
            ) : null}
          </button>
          <button
            className="btn-primary"
            onClick={handleNewProduct}
          >
            <Plus size={18} />
            Novo Produto
          </button>
        </div>
      </div>

      {loadError && catalog.length === 0 && (
        <div className="produtos-cache-empty" role="alert">
          <p>{loadError}</p>
          <button type="button" className="btn-secondary" onClick={loadCatalog}>
            Tentar novamente
          </button>
        </div>
      )}

      {/* Main List Container */}
      <div className="requisitions-card">
        {/* Search and Filters */}
        <div className="requisitions-toolbar">
          <div className={`search-wrapper${searchTerm ? ' has-term' : ''}`}>
            <Search className={`search-icon ${searchTerm ? 'active' : ''}`} size={16} />
            <input
              type="text"
              placeholder="Pesquisar produtos por nome, SKU ou código de barras..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input search-input--camera"
              disabled={loading && catalog.length === 0}
            />
            <QrCodeScannerButton
              className="search-camera-btn"
              title="Ler QR Code ou código de barras"
              onScan={(code) => setSearchTerm(String(code || '').trim())}
            />
            {searchTerm && (
              <button
                className="clear-search"
                onClick={() => setSearchTerm('')}
              >
                <X size={14} />
              </button>
            )}
          </div>
          {selectedBrand ? (
            <button
              type="button"
              className="produtos-brand-chip"
              onClick={openBrandModal}
              title="Alterar filtro de marca"
            >
              <Tag size={14} />
              {selectedBrand.name}
              <span
                className="produtos-brand-chip__clear"
                role="button"
                tabIndex={0}
                aria-label="Limpar marca"
                onClick={(e) => {
                  e.stopPropagation()
                  clearBrandFilter()
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    clearBrandFilter()
                  }
                }}
              >
                <X size={12} />
              </span>
            </button>
          ) : null}
          <div className="filter-buttons">
            <div className="menu-wrapper" ref={itemsPerPageMenuRef}>
              <button
                className={`menu-trigger ${openItemsPerPageDropdown ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation()
                  setOpenItemsPerPageDropdown(!openItemsPerPageDropdown)
                }}
              >
                {itemsPerPage} por página
                <MoreVertical size={16} style={{ marginLeft: '8px' }} />
              </button>
              {openItemsPerPageDropdown && (
                <div 
                  className="dropdown-menu"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <button 
                    className={`dropdown-item ${itemsPerPage === 25 ? 'active' : ''}`}
                    onClick={() => {
                      setItemsPerPage(25)
                      setCurrentPage(1)
                      setOpenItemsPerPageDropdown(false)
                    }}
                  >
                    25 por página
                  </button>
                  <button 
                    className={`dropdown-item ${itemsPerPage === 50 ? 'active' : ''}`}
                    onClick={() => {
                      setItemsPerPage(50)
                      setCurrentPage(1)
                      setOpenItemsPerPageDropdown(false)
                    }}
                  >
                    50 por página
                  </button>
                  <button 
                    className={`dropdown-item ${itemsPerPage === 100 ? 'active' : ''}`}
                    onClick={() => {
                      setItemsPerPage(100)
                      setCurrentPage(1)
                      setOpenItemsPerPageDropdown(false)
                    }}
                  >
                    100 por página
                  </button>
                  <button 
                    className={`dropdown-item ${itemsPerPage === 200 ? 'active' : ''}`}
                    onClick={() => {
                      setItemsPerPage(200)
                      setCurrentPage(1)
                      setOpenItemsPerPageDropdown(false)
                    }}
                  >
                    200 por página
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <DataTable
          columns={columnsWithActions}
          data={produtos}
          loading={loading}
          emptyMessage="Nenhum produto encontrado"
          pagination={{
            current: currentPage,
            totalPages: Math.ceil(allProdutos.length / itemsPerPage),
            total: allProdutos.length,
            from: allProdutos.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0,
            to: Math.min(currentPage * itemsPerPage, allProdutos.length),
            hasPrev: currentPage > 1,
            hasNext: currentPage < Math.ceil(allProdutos.length / itemsPerPage),
            onPrev: () => setCurrentPage(prev => Math.max(1, prev - 1)),
            onNext: () => setCurrentPage(prev => Math.min(Math.ceil(allProdutos.length / itemsPerPage), prev + 1))
          }}
          onRowClick={(row, event) => {
            // Não abrir se clicar no botão de ações
            if (event.target.closest('.product-actions-cell') || event.target.closest('.menu-wrapper')) {
              return
            }
            // Abrir em nova aba
            const url = `/erp/produtos/produtos/${row.id}`
            window.open(url, '_blank')
          }}
        />
      </div>

      {/* Modal de Criação/Edição */}
      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          resetForm()
        }}
        title="Editar Produto"
        size="large"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => {
                setModalOpen(false)
                resetForm()
              }}
            >
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
            >
              Salvar
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <FormField
              label="SKU"
              name="sku"
              value={formData.sku}
              onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase() })}
              error={errors.sku}
              required
              disabled={!editingProduto}
            />

            <FormField
              label="Código de Barras"
              name="barcode"
              value={formData.barcode}
              onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
              error={errors.barcode}
            />
          </div>

          <FormField
            label="Nome"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={errors.name}
            required
          />

          <FormField
            label="Descrição"
            name="description"
            type="textarea"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            error={errors.description}
            rows={3}
            disabled={!canEditDescription}
          />

          <div className="form-row">
            {hideProductCost ? null : (
            <FormField
              label="Preço de Custo"
              name="costPrice"
              type="number"
              step="0.01"
              value={formData.costPrice}
              onChange={(e) => setFormData({ ...formData, costPrice: e.target.value })}
              error={errors.costPrice}
            />
            )}

            <FormField
              label="Preço de Venda"
              name="price"
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              error={errors.price}
              required
            />
          </div>

          <div className="form-row">
            <FormField
              label="Grupo"
              name="groupId"
              type="select"
              value={formData.groupId}
              disabled={!canEditGroup}
              onChange={(e) => setFormData({ ...formData, groupId: e.target.value })}
              options={[
                { value: '', label: 'Selecione...' },
                ...grupos.map(group => ({
                  value: group.id,
                  label: group.name
                }))
              ]}
            />

            <FormField
              label="Marca"
              name="brandId"
              type="select"
              value={formData.brandId}
              disabled={!canEditBrand}
              onChange={(e) => setFormData({ ...formData, brandId: e.target.value })}
              options={[
                { value: '', label: 'Selecione...' },
                ...marcas.map(brand => ({
                  value: brand.id,
                  label: brand.name
                }))
              ]}
            />

            <FormField
              label="Unidade"
              name="unitId"
              type="select"
              value={formData.unitId}
              onChange={(e) => setFormData({ ...formData, unitId: e.target.value })}
              options={[
                { value: '', label: 'Selecione...' },
                ...unidades.map(unit => ({
                  value: unit.id,
                  label: `${unit.name} (${unit.code || ''})`
                }))
              ]}
            />
          </div>

          <div className="form-row">
            <FormField
              label="Estoque Mínimo"
              name="minStock"
              type="number"
              value={formData.minStock}
              onChange={(e) => setFormData({ ...formData, minStock: e.target.value })}
              error={errors.minStock}
            />

            <FormField
              label="Estoque Máximo"
              name="maxStock"
              type="number"
              value={formData.maxStock}
              onChange={(e) => setFormData({ ...formData, maxStock: e.target.value })}
              error={errors.maxStock}
            />
          </div>

          <FormField
            label="Status"
            name="active"
            type="select"
            value={formData.active ? 'true' : 'false'}
            onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
            options={[
              { value: 'true', label: 'Ativo' },
              { value: 'false', label: 'Inativo' }
            ]}
          />

          {errors.submit && (
            <div className="form-error-message">{errors.submit}</div>
          )}
        </form>
      </Modal>

      <ERPSelectSearchModal
        isOpen={brandModalOpen}
        onClose={() => setBrandModalOpen(false)}
        title="Filtrar por marca"
        subtitle="Busque e selecione uma marca"
        icon={<Tag size={22} />}
        searchPlaceholder="Pesquisar marca..."
        searchTerm={brandSearchTerm}
        onSearchChange={setBrandSearchTerm}
      >
        <ERPSelectModalItem
          selected={!selectedBrand}
          onClick={() => {
            setSelectedBrand(null)
            setBrandModalOpen(false)
          }}
          avatar={<Tag size={18} />}
          name="Todas as marcas"
          checkIcon={<Check size={18} />}
        />
        {filteredBrands.length === 0 ? (
          <ERPSelectModalEmpty
            icon={<Tag size={40} />}
            message={
              brandSearchTerm.trim()
                ? 'Nenhuma marca encontrada'
                : 'Nenhuma marca cadastrada'
            }
          />
        ) : (
          filteredBrands.map((brand) => (
            <ERPSelectModalItem
              key={brand.id}
              selected={selectedBrand && String(selectedBrand.id) === String(brand.id)}
              onClick={() => {
                setSelectedBrand({ id: brand.id, name: brand.name })
                setBrandModalOpen(false)
              }}
              avatar={<Tag size={18} />}
              name={brand.name}
              detail={brand.code ? `Código ${brand.code}` : undefined}
              checkIcon={<Check size={18} />}
            />
          ))
        )}
      </ERPSelectSearchModal>
    </div>
  )
}

export default ProdutosPage

